import { useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import HttpIcon from '@mui/icons-material/Http';
import LockIcon from '@mui/icons-material/Lock';
import TerminalIcon from '@mui/icons-material/Terminal';
import {
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { NDConnectReady, NDConnectSSHReady } from '@bumble/app/components/network-devices';
import { NetworkInterfaceSelect, NetworkInterfaceValue } from '@bumble/app/components/NetworkInterfaceSelect';

interface NDConnectProps {
  handleClose: () => void;
  cpeId: string;
  macAddr: string;
	targetIP: string;
}

export const NDConnect = ({ handleClose, cpeId, macAddr, targetIP }: NDConnectProps) => {
  const api = useApi();

  type PortKey = 'http' | 'https' | 'ssh';

  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [showReadyDialog, setShowReadyDialog] = useState<boolean>(false);
  const [readyUrl, setReadyUrl] = useState<string>('');
  const [showSSHReadyDialog, setShowSSHReadyDialog] = useState<boolean>(false);
  const [sshData, setSSHData] = useState<any>(null);
  const [ports, setPorts] = useState<Record<PortKey, string>>({
    http: '80',
    https: '443',
    ssh: '22',
  });
  const [selectedInterface, setSelectedInterface] = useState<NetworkInterfaceValue>('default');

  const connectSession = useMutation(api.connectNetworkRemoteAccessApiV1ConnectNetworkRemoteAccessPost, {
    onSuccess(res: any) {
      setTimeout(() => {
        toast.success('A new connection added successfully.');
        setReadyUrl(res.data?.web_url ?? '');
        setLoadingModalOpen(false);
        setShowReadyDialog(true);
      }, 10000);
    },
    onError(error: any) {
      setLoadingModalOpen(false);
      apiError(error);
    },
  });

  const connectSSHSession = useMutation(api.connectRawSshAccessApiV1ConnectRawSshAccessPost, {
    onSuccess(res: any) {
      setTimeout(() => {
        toast.success('A new connection added successfully.');
        setSSHData(res.data);
        setLoadingModalOpen(false);
        setShowSSHReadyDialog(true);
      }, 10000);
    },
    onError(error: any) {
      setLoadingModalOpen(false);
      apiError(error);
    },
  });

  const handleSubmit = (protocol: PortKey) => {
    setLoadingModalOpen(true);
    if (protocol === 'ssh') {
      connectSSHSession.mutate({
        cpe_id: cpeId,
        mac_addr: macAddr,
        target_ip: targetIP,
        target_port: parseInt(ports[protocol]),
        interface: selectedInterface,
      });
    } else {
      connectSession.mutate({
        cpe_id: cpeId,
        mac_addr: macAddr,
        target_ip: targetIP,
        target_port: parseInt(ports[protocol]),
        protocol,
        interface: selectedInterface,
      });
    }
  };

  const connections: { protocol: PortKey; label: string; description: string, icon: any }[] = [
    {
      label: 'HTTP Connect',
      protocol: 'http',
      description: 'Connect by a browser session',
      icon: <HttpIcon fontSize='small' />,
    },
    {
      label: 'HTTPS Connect',
      protocol: 'https',
      description: 'Connect by a browser session',
      icon: <LockIcon fontSize='small' />,
    },
    {
      label: 'SSH Connect',
      protocol: 'ssh',
      description: 'Connect by a SSH session',
      icon: <TerminalIcon fontSize='small' />,
    },
  ];

  const closeConsole = () => {
    setShowReadyDialog(false);
    handleClose();
  };

  const closeSSHConsole = () => {
    setShowSSHReadyDialog(false);
    handleClose();
  };

  return (
    <>
      {loadingModalOpen && <PageLoading content="Connecting..." />}
      <Typography variant="body2" sx={{ mb: 4 }}>
        Click an icon to connect.
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
        Network Interface
      </Typography>
      <NetworkInterfaceSelect value={selectedInterface} onChange={setSelectedInterface} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: '50px',
          mb: '50px'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          {connections.map(({ label, description, icon, protocol }) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <Button
                variant="outlined"
                startIcon={icon}
                onClick={() => { handleSubmit(protocol) }}
                sx={{
                  minWidth: 170,
                  height: 40,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  borderRadius: 1,
                  borderColor: 'primary.main',
                  color: 'text.primary',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'primary.50',
                  },
                }}
              >
                {label}
              </Button>
              <TextField
                value={ports[protocol]}
                onChange={(e) =>
                  setPorts(prev => ({ ...prev, [protocol]: e.target.value }))
                }
                size="small"
                sx={{
                  width: 80,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    fontWeight: 600,
                  },
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {showReadyDialog && (
        <NDConnectReady
          handleClose={closeConsole}
          readyUrl={readyUrl}
          showReadyDialog={showReadyDialog}
        />
      )}
      {showSSHReadyDialog && (
        <NDConnectSSHReady
          handleClose={closeSSHConsole}
          data={sshData}
          showReadyDialog={showSSHReadyDialog}
        />
      )}
    </>
  );
};
